import { STAGE_LAYOUTS, OVERLAYS, SHOW_SHORT } from '../live/stageConfig';
import { SHOWS } from '../lib/shows';

const loadImg = (src) =>
  new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });

const fmtDate = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const weekdayOf = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();

export async function generateResultImage(show, letter, digits, dateISO) {
  const theme = SHOWS.find((s) => s.id === show).theme;
  const layout = STAGE_LAYOUTS[theme];
  await document.fonts.load('80px Anton').catch(() => {});
  await document.fonts.ready.catch(() => {});
  const img = await loadImg(layout.image);
  const canvas = document.createElement('canvas');
  canvas.width = 1536;
  canvas.height = 1024;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, 1536, 1024);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const gold = (size) => {
    ctx.fillStyle = '#F5C542';
    ctx.font = `${size}px Anton`;
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 4;
  };
  const short = SHOW_SHORT[show];
  const weekday = weekdayOf(dateISO);

  const b = OVERLAYS.banner;
  gold(38);
  ctx.fillText(`GOLDEN RAJA SHINE ${weekday} WEEKLY DRAW`, b.x + b.w / 2, b.y + b.h / 2);

  const w = layout.wheel;
  ctx.shadowBlur = 0;
  const R = w.d / 2;
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(w.cx, w.cy, R, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#D8DCE0';
  ctx.lineWidth = 2;
  ctx.stroke();

  const WHEEL = 'ABCDEFGHJKL'.split('');
  const SEG = (Math.PI * 2) / WHEEL.length;
  const tIdx = Math.max(WHEEL.indexOf(letter), 0);
  const rot = -tIdx * SEG;
  const s = w.d / 340;
  ctx.fillStyle = '#0B3D1E';
  ctx.font = `${Math.round(62 * s)}px Anton`;
  WHEEL.forEach((l, k) => {
    const a = k * SEG + rot;
    const lx = w.cx + R * 0.78 * Math.sin(a);
    const ly = w.cy - R * 0.78 * Math.cos(a);
    ctx.save();
    ctx.translate(lx, ly);
    ctx.rotate(a);
    ctx.fillText(l, 0, 0);
    ctx.restore();
  });

  const nGrad = ctx.createLinearGradient(w.cx, w.cy - R, w.cx, w.cy);
  nGrad.addColorStop(0, '#FF3B4E');
  nGrad.addColorStop(1, '#B90D1D');
  ctx.fillStyle = nGrad;
  ctx.beginPath();
  ctx.moveTo(w.cx - 9 * s, w.cy);
  ctx.lineTo(w.cx + 9 * s, w.cy);
  ctx.lineTo(w.cx + 2 * s, w.cy - R * 0.56);
  ctx.lineTo(w.cx - 2 * s, w.cy - R * 0.56);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(w.cx - 6 * s, w.cy);
  ctx.lineTo(w.cx + 6 * s, w.cy);
  ctx.lineTo(w.cx, w.cy + 26 * s);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.arc(w.cx, w.cy, 13 * s, 0, Math.PI * 2);
  ctx.fillStyle = '#B90D1D';
  ctx.fill();
  ctx.strokeStyle = '#E7C463';
  ctx.lineWidth = 2.5 * s;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(w.cx, w.cy, 4.5 * s, 0, Math.PI * 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();

  layout.boxes.forEach((box, i) => {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(box.x, box.y, box.w, box.h);
    ctx.fillStyle = '#0B3D1E';
    ctx.font = `${Math.round(box.h * 0.62)}px Anton`;
    ctx.fillText(digits[i], box.x + box.w / 2, box.y + box.h / 2 + 4);
  });

  const sp = OVERLAYS.sidePlate;
  ctx.save();
  ctx.translate(sp.x + sp.w / 2, sp.y + sp.h / 2);
  ctx.rotate(-Math.PI / 2);
  gold(40);
  ctx.fillText(`${weekday.slice(0, 3)} · ${short}`, 0, 0);
  ctx.restore();

  const bc = OVERLAYS.bottomCircle;
  gold(42);
  ctx.fillText(short.split(' ')[0], bc.x + bc.w / 2, bc.y + bc.h / 2 - 14);
  gold(22);
  ctx.fillText(short.split(' ')[1], bc.x + bc.w / 2, bc.y + bc.h / 2 + 22);

  const dp = OVERLAYS.datePlate;
  gold(34);
  ctx.fillText(`DRAW DATE : ${fmtDate(dateISO)}`, dp.x + dp.w / 2, dp.y + dp.h / 2);

  if (!layout.drawTimeBaked) {
    const tp = OVERLAYS.timePlate;
    ctx.fillStyle = '#F5F5F5';
    ctx.font = '34px Anton';
    ctx.fillText(`DRAW TIME / ${short}`, tp.x + tp.w / 2, tp.y + tp.h / 2);
  }
  return canvas;
}

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('blob failed'))), 'image/jpeg', 0.92);
  });

const triggerDownload = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

export async function downloadResultImage(show, letter, digits, dateISO) {
  const canvas = await generateResultImage(show, letter, digits, dateISO);
  const blob = await canvasToBlob(canvas);
  triggerDownload(blob, `golden-raja-${show}-result-${fmtDate(dateISO)}.jpg`);
}

export function shareMessage(show, letter, digits, dateISO) {
  return `🏆 GOLDEN RAJA ${SHOW_SHORT[show]} RESULT — ${fmtDate(dateISO)}\n🎯 WINNING NUMBER: ${letter} ${digits}\nhttps://goldenraja.in/live`;
}

export async function telegramAnnounce(show, letter, digits, dateISO, headers) {
  const canvas = await generateResultImage(show, letter, digits, dateISO);
  const blob = await canvasToBlob(canvas);
  const fd = new FormData();
  fd.append('photo', blob, 'result.jpg');
  fd.append('caption', shareMessage(show, letter, digits, dateISO));
  await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/telegram/announce`, {
    method: 'POST',
    headers,
    body: fd,
  }).then((r) => {
    if (!r.ok) throw new Error('telegram failed');
  });
}

export async function shareResultImage(show, letter, digits, dateISO, app) {
  const text = shareMessage(show, letter, digits, dateISO);
  const canvas = await generateResultImage(show, letter, digits, dateISO);
  const blob = await canvasToBlob(canvas);
  const file = new File([blob], `golden-raja-${show}-result-${fmtDate(dateISO)}.jpg`, {
    type: 'image/jpeg',
  });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      // image + caption text — WhatsApp shows the text below the image
      await navigator.share({ files: [file], text });
      return 'shared';
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled';
    }
  }
  triggerDownload(blob, file.name);
  const url =
    app === 'telegram'
      ? telegramLink(text)
      : app === 'facebook'
        ? facebookLink(text)
        : whatsappLink(text);
  window.open(url, '_blank', 'noopener');
  return 'fallback';
}

export const whatsappLink = (msg) => `https://wa.me/?text=${encodeURIComponent(msg)}`;
export const facebookLink = (msg) =>
  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://goldenraja.in/live')}&quote=${encodeURIComponent(msg)}`;
export const telegramLink = (msg) =>
  `https://t.me/share/url?url=${encodeURIComponent('https://goldenraja.in/live')}&text=${encodeURIComponent(msg)}`;
